// @ts-nocheck
/* eslint-disable */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { cn } from '@frontend/shadcn/lib/utils';
import { PlusIcon } from '@heroicons/react/24/outline';
import { CreatorPortalPreview } from './components/CreatorPortalPreview';
import { TProject } from '../../types';
import { SidebarLayout } from '../components/SidebarLayout';
import { StageNameSection } from './components/StageNameSection';
import { StatusSection } from './components/StatusSection';
import { TaskSection } from './components/TaskSection';
import { CustomStagesSaveBar } from './components/CustomStagesSaveBar';
import { CustomStagesProvider, useCustomStages } from './context/CustomStagesContext';
import { SettingsSection } from './components/SettingsSection';
import { TemplatesDrawer } from './components/TemplatesDrawer';
import type { GetStageTemplates_getStageTemplates as IStageTemplate } from '@frontend/app/queries/types/GetStageTemplates';

interface Props {
  mode?: 'add' | 'edit';
  project?: TProject;
  onSave?: () => void;
  refetchProjects?: () => void;
}

interface StageContentProps {
  onSave?: () => void;
  stageId: number;
  project?: TProject;
  isNew?: boolean;
  editedName: string;
  setEditedName: (name: string) => void;
  newStageStatuses: Array<{ id: number; name: string; priority: number }>;
  setNewStageStatuses: (statuses: Array<{ id: number; name: string; priority: number }>) => void;
  tasks: Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: any[];
  }>;
  setTasks: (tasks: Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: any[];
  }>) => void;
  taskSettings?: {
    initialStatuses: Array<string>;
    finalStatus: string | null;
  };
  setTaskSettings: (settings: {
    initialStatuses: Array<string>;
    finalStatus: string | null;
  }) => void;
}

const StageContent: React.FC<StageContentProps> = ({
  onSave,
  stageId,
  project,
  isNew,
  editedName,
  newStageStatuses,
  setEditedName,
  setNewStageStatuses,
  tasks,
  setTasks,
  taskSettings,
  setTaskSettings,
}) => {
  const { setHasUnsavedChanges, selectedStage, updateStage } = useCustomStages();
  
  // Local state to track the actual updated tasks
  const [localTasks, setLocalTasks] = useState(tasks);

  // Use useRef to store initial values for each stage
  const initialValuesMapRef = useRef<{ [key: number]: {
    name: string;
    statuses: Array<any>;
    tasks: Array<any>;
    taskSettings: { initialStatuses: Array<string>; finalStatus: string | null };
  } }>({});

  // Track if we've initialized the current stage
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize localTasks only once
  useEffect(() => {
    if (selectedStage) {
      setLocalTasks(tasks);
    }
  }, [selectedStage?.id]); // Only when stage changes

  // Update initial values when selectedStage changes or component mounts
  useEffect(() => {
    if (selectedStage) {
      // Only set initial values for this stage if not already set
      if (!initialValuesMapRef.current[selectedStage.id]) {
        initialValuesMapRef.current[selectedStage.id] = {
          name: selectedStage.name,
          statuses: selectedStage.statuses || [],
          tasks: selectedStage.tasks || [],
          taskSettings: selectedStage.taskSettings || { initialStatuses: [], finalStatus: null }
        };

        // Initialize local state with stage data
        setEditedName(selectedStage.name);
        setNewStageStatuses(selectedStage.statuses || []);
        setTasks(selectedStage.tasks || []);
        setTaskSettings(selectedStage.taskSettings || { initialStatuses: [], finalStatus: null });
      }
      // Mark as initialized and reset unsaved changes
      setIsInitialized(true);
      setHasUnsavedChanges(false);
    } else {
      setIsInitialized(false);
    }
  }, [selectedStage?.id]); // Only depend on the ID, not the entire stage object

  // Get current stage's initial values
  const currentStageInitialValues = useMemo(() => {
    if (!selectedStage || !isInitialized) {
      // Return current prop values to prevent false change detection
      return {
        name: editedName || '',
        statuses: newStageStatuses || [],
        tasks: tasks || [],
        taskSettings: taskSettings || { initialStatuses: [], finalStatus: null }
      };
    }
    return initialValuesMapRef.current[selectedStage.id];
  }, [selectedStage?.id, isInitialized, editedName, newStageStatuses, tasks, taskSettings]);

  // Check for changes using the ref values
  useEffect(() => {
    // Skip change detection until initialized
    if (!isInitialized || !currentStageInitialValues) return;

    const hasNameChanged = (editedName || '') !== (currentStageInitialValues.name || '');
    const hasStatusesChanged = JSON.stringify(newStageStatuses || []) !== JSON.stringify(currentStageInitialValues.statuses || []);
    const hasTasksChanged = JSON.stringify(tasks || []) !== JSON.stringify(currentStageInitialValues.tasks || []);
    const hasTaskSettingsChanged = JSON.stringify(taskSettings || { initialStatuses: [], finalStatus: null }) !== JSON.stringify(currentStageInitialValues.taskSettings || { initialStatuses: [], finalStatus: null });

    const hasChanges = hasNameChanged || hasStatusesChanged || hasTasksChanged || hasTaskSettingsChanged;
    setHasUnsavedChanges(hasChanges);
  }, [editedName, newStageStatuses, tasks, taskSettings, setHasUnsavedChanges, selectedStage?.id, currentStageInitialValues, isInitialized]);

  const handleCancel = () => {
    if (!selectedStage || !initialValuesMapRef.current[selectedStage.id]) return;

    const originalValues = initialValuesMapRef.current[selectedStage.id];

    // Update through context to ensure all components get the update
    updateStage(selectedStage.id, {
      name: originalValues.name,
      statuses: originalValues.statuses,
      tasks: originalValues.tasks,
      taskSettings: originalValues.taskSettings
    });

    // Also update local state to ensure immediate UI update
    setEditedName(originalValues.name);
    setNewStageStatuses(originalValues.statuses);
    setTasks(originalValues.tasks);
    setTaskSettings(originalValues.taskSettings);

    setHasUnsavedChanges(false);
  };

  const handleSave = () => {
    if (!selectedStage) return;

    // After successful save, update the initial values for this stage
    const newValues = {
      name: editedName,
      statuses: newStageStatuses,
      tasks: localTasks,
      taskSettings,
    };
    initialValuesMapRef.current[selectedStage.id] = newValues;
    setHasUnsavedChanges(false);
    onSave();
  };

  return (
    <div className="flex justify-between h-full min-h-screen">
      <div className="flex flex-col gap-4 flex-1 pb-[30px] min-h-max">
        <CustomStagesSaveBar
          onSave={handleSave}
          onCancel={handleCancel}
          editedName={editedName}
          statuses={newStageStatuses}
          tasks={localTasks}
          taskSettings={taskSettings}
        />
        <StageNameSection
          editedName={editedName}
          setEditedName={setEditedName}
          isNew={isNew}
        />
        <SettingsSection
          tasks={tasks}
          setTasks={setLocalTasks}
          taskSettings={taskSettings}
          setTaskSettings={setTaskSettings}
        />
      </div>
    </div>
  );
};

const CustomStagesContent: React.FC<Props> = ({ project, onSave }) => {
  const { stages, selectedStageId, setSelectedStageId, isNewStage, createNewStage, refetchStages } = useCustomStages();
  const [editedName, setEditedName] = useState('New Custom Stage');
  const [newStageStatuses, setNewStageStatuses] = useState<Array<{ id: number; name: string; priority: number }>>([]);
  const [tasks, setTasks] = useState<Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: any[];
  }>>([]);
  const [taskSettings, setTaskSettings] = useState<{
    initialStatuses: Array<string>;
    finalStatus: string | null;
  }>({
    initialStatuses: [],
    finalStatus: null
  });
  const [isTemplatesDrawerOpen, setIsTemplatesDrawerOpen] = useState(false);
  const selectedStage = stages.find((stage) => stage.id === selectedStageId);

  // Update local state when selected stage changes
  useEffect(() => {
    if (selectedStage) {
      setEditedName(selectedStage.name);
      setNewStageStatuses(selectedStage.statuses || []);
      setTasks(selectedStage.tasks || []);
      setTaskSettings(selectedStage.taskSettings || { initialStatuses: [], finalStatus: null });
    }
  }, [selectedStage]); // This will update when template is applied since it changes the stage

  useEffect(() => {
    if (!selectedStageId) {
      setSelectedStageId(stages[0]?.id || null);
      // setEditedName('New Custom Stage');
      // setNewStageStatuses([]);
      // setTasks([]);
    }
  }, [selectedStageId, stages]);

  // Add debug log
  useEffect(() => {
  }, [newStageStatuses]);

  const handleTemplateSelect = (template: IStageTemplate) => {
    if (selectedStageId) {
      // Use the context method to apply template
      applyTemplate(selectedStageId, {
        name: template.name,
        description: template.description,
        statuses: template.statuses
      });

      // Update local state to reflect changes
      setEditedName(template.name);
      setNewStageStatuses(template.statuses.map((status, index) => ({
        id: Date.now() + index,
        name: status.name,
        priority: index + 1,
        bgColor: status.bgColor
      })));
    }
  };

  if (!project) {
    return <div>No project found</div>;
  }

  const previewSection = (
    <div className="hidden lg:block lg:w-[465px] lg:min-w-[465px] border-l border-grey-3 h-full overflow-y-auto">
      <CreatorPortalPreview
        project={project}
        stageName={selectedStage?.name}
        taskDescription={selectedStage?.tasks[0]?.name}
        formFields={selectedStage?.tasks[0]?.fields}
        buttonLabel="Submit"
      />
    </div>
  );

  const sections = useMemo(() => {
    const allSections = [
      ...stages.map((stage) => ({
        id: stage.id.toString(),
        label: stage.name,
        component: (
          <StageContent
            onSave={onSave}
            stageId={stage.id}
            project={project}
            editedName={stage.name}
            newStageStatuses={stage.statuses || []}
            setEditedName={setEditedName}
            setNewStageStatuses={setNewStageStatuses}
            tasks={stage.tasks}
            setTasks={setTasks}
            taskSettings={stage.taskSettings || { initialStatuses: [], finalStatus: null }}
            setTaskSettings={setTaskSettings}
          />
        ),
        rightSection: previewSection,
      }))
    ];

    return allSections;
  }, [stages, onSave, project, previewSection]);

  // Get the active section - either 'new' or an existing stage
  // const activeSectionId = isNewStage ? 'new' : selectedStageId?.toString();
  const activeSectionId = selectedStageId?.toString();

  return (
    <SidebarLayout
      sections={sections}
      activeSectionId={activeSectionId}
      onlyShowActiveSection
      onSectionClick={(sectionId) => {
        setSelectedStageId(sectionId === 'new' ? null : parseInt(sectionId));
        // Reset all form data when clicking on "New Custom Stage"
        if (sectionId === 'new') {
          setEditedName('New Custom Stage');
          setNewStageStatuses([]);
          setTasks([]);
          setTaskSettings({ initialStatuses: [], finalStatus: null });
        }
      }}
      renderSectionLabel={(section) => (
        <div className={cn(
          'flex items-center gap-2 p-2 rounded-lg',
          section.isAddNew && 'bg-gray-100'
        )}
        >
          {section.isAddNew && <PlusIcon className="w-4 h-4" />}
          <span>{section.label}</span>
        </div>
      )}
      renderContent={() => (
        <>
          <StageNameSection
            editedName={editedName}
            setEditedName={setEditedName}
            isNew={isNewStage}
          />
          <StatusSection />
          <TaskSection />
          <SettingsSection
            tasks={tasks}
            setTasks={setTasks}
            taskSettings={taskSettings}
            setTaskSettings={setTaskSettings}
          />
          <TemplatesDrawer
            open={isTemplatesDrawerOpen}
            onOpenChange={setIsTemplatesDrawerOpen}
            onSelect={handleTemplateSelect}
          />
        </>
      )}
    />
  );
};

export const CustomStagesTab: React.FC<Props> = ({ project, ...props }) => {
  return (
    <CustomStagesProvider programId={project?.id}>
      <CustomStagesContent project={project} {...props} />
    </CustomStagesProvider>
  );
};
